export enum PostHogEvent {
  AI_SUMMIT_SIGNUP_INTEREST = 'ai-summit-signup-interest',
  ENTERPRISE_PRICING_CTA_ACTIVATED = 'enterprise_pricing_cta_activated',
  PRICING_BILLING_FREQUENCY_SELECTED = 'pricing_billing_frequency_selected',
  ASSET_DOWNLOADED = 'asset_downloaded',
  MARKETING_APP_DOWNLOAD_CLICKED = 'marketing_app_download_clicked',
  FILE_UPLOAD_FAILED = 'file_upload_failed',
  FILE_UPLOADED = 'file_uploaded',
  MAIN_IMAGE_REPLACED = 'main_image_replaced',
  LEGACY_PRODUCT_BULK_MIGRATION_FINISHED = 'legacy_product_bulk_migration_finished',
  LEGACY_PRODUCT_BULK_MIGRATION_STARTED = 'legacy_product_bulk_migration_started',
  TRASH_EMPTIED = 'trash_emptied',
  TRASH_ITEM_RESTORED = 'trash_item_restored',
  UPGRADE_CTA_ACTIVATED = 'upgrade_cta_activated',
  UPGRADE_CTA_SHOWN = 'upgrade_cta_shown',
  ORG_CHAT_GENERATE_RESPONSE_ERROR = 'org_chat_generate_response_error',
  ORG_CHAT_RESULT_FEEDBACK = 'org_chat_result_feedback',
  ORG_CHAT_THREAD_ERROR = 'org_chat_thread_error',
  WORKSPACE_UPLOAD_FAILED = 'workspace_upload_failed',
  /** A My Drive upload refused in the browser before any request (#7107). */
  WORKSPACE_UPLOAD_PREFLIGHT_BLOCKED = 'workspace_upload_preflight_blocked',
  YOUR_IMAI_CHANNEL_PICKED = 'your_imai_channel_picked',
  YOUR_IMAI_EXAMPLE_LINK_OPENED = 'your_imai_example_link_opened',
  YOUR_IMAI_PLAN_CARD_CHOOSE_CLICKED = 'your_imai_plan_card_choose_clicked',
  YOUR_IMAI_WELCOME_PLAN_CARD_VIEWED = 'your_imai_welcome_plan_card_viewed',
}

export type PricingBillingFrequency = 'monthly' | 'annual';
export type PricingSurface = 'public-pricing' | 'workspace-pricing';

interface AnalyticsCaptureClient {
  capture: (
    event: PostHogEvent,
    properties: { frequency: PricingBillingFrequency; surface: PricingSurface }
  ) => void;
}

export const capturePricingBillingFrequencySelected = (
  analytics: AnalyticsCaptureClient,
  frequency: PricingBillingFrequency,
  surface: PricingSurface
): void => {
  analytics.capture(PostHogEvent.PRICING_BILLING_FREQUENCY_SELECTED, {
    frequency,
    surface,
  });
};

export type UpgradeCtaSurface =
  'composer' | 'composer-send-blocked' | 'sidebar-rail' | 'sidebar-panel';

export type UpgradeCtaProperties = {
  surface: UpgradeCtaSurface;
  state: 'blocked' | 'team-limit' | 'low';
  /** `member` for a used-up team cap, which only an admin can raise. */
  audience: 'buyer' | 'member';
};

interface UpgradeCtaCaptureClient {
  capture: (event: PostHogEvent, properties: UpgradeCtaProperties) => void;
}

export const captureUpgradeCta = (
  analytics: UpgradeCtaCaptureClient | null | undefined,
  action: 'shown' | 'activated',
  properties: UpgradeCtaProperties
): void => {
  analytics?.capture(
    action === 'activated'
      ? PostHogEvent.UPGRADE_CTA_ACTIVATED
      : PostHogEvent.UPGRADE_CTA_SHOWN,
    properties
  );
};

/** Where a Your IMAI plan card was shown: the welcome, or IMAI's reply. */
export type YourImaiPlanCardSource = 'your_imai_welcome' | 'your_imai_feed';

export type YourImaiPlanChoiceProperties = {
  source: YourImaiPlanCardSource;
  plan: 'starter' | 'pro' | 'ultra';
  billingCycle: PricingBillingFrequency;
};

interface YourImaiCaptureClient {
  capture: (event: PostHogEvent, properties?: Record<string, string>) => void;
}

/** Events carry fixed ids and enums only, never workspace or member data. */
export const captureYourImaiWelcomePlanCardViewed = (
  analytics: YourImaiCaptureClient | null | undefined
): void => {
  analytics?.capture(PostHogEvent.YOUR_IMAI_WELCOME_PLAN_CARD_VIEWED);
};

export const captureYourImaiPlanChosen = (
  analytics: YourImaiCaptureClient | null | undefined,
  properties: YourImaiPlanChoiceProperties
): void => {
  analytics?.capture(PostHogEvent.YOUR_IMAI_PLAN_CARD_CHOOSE_CLICKED, {
    ...properties,
  });
};

export const captureYourImaiExampleOpened = (
  analytics: YourImaiCaptureClient | null | undefined,
  seedId: string
): void => {
  analytics?.capture(PostHogEvent.YOUR_IMAI_EXAMPLE_LINK_OPENED, { seedId });
};

export type YourImaiChannelPick = 'whatsapp' | 'imessage' | 'contact_card';

export type YourImaiChannelPickProperties = {
  pick: YourImaiChannelPick;
  /** The channel listed first for this device. */
  suggested: 'whatsapp' | 'imessage';
  surface: 'popover' | 'sheet';
};

export const captureYourImaiChannelPicked = (
  analytics: YourImaiCaptureClient | null | undefined,
  properties: YourImaiChannelPickProperties
): void => {
  analytics?.capture(PostHogEvent.YOUR_IMAI_CHANNEL_PICKED, { ...properties });
};
